import type { ReactNode } from 'react';

import { createContext, use, useMemo } from 'react';

import type { ConditionConfig, ConditionMap } from './types';

import { evaluateCondition } from './utils';

/**
 * Internal context value that holds both the context and conditions map.
 */
type ConditionContextValue<TContext, TConditions extends ConditionMap<TContext>> = {
    context: TContext;
    conditions: TConditions;
};

/**
 * Creates a typed condition context with Provider and hooks.
 * Apps use this factory to create their own condition system with domain-specific context and conditions.
 *
 * @example
 * // In your app's conditions config:
 * type MyConditionContext = BaseConditionContext & {
 *     projectFlags: { priceQuote: boolean; planning: boolean };
 * };
 *
 * const myConditions = {
 *     canAccessQuotation: (ctx: MyConditionContext) => ctx.projectFlags.priceQuote,
 *     canAccessSchedule: (ctx: MyConditionContext) => ctx.projectFlags.planning,
 * } as const;
 *
 * export const {
 *     ConditionProvider,
 *     useConditionContext,
 *     useCondition,
 * } = createConditionContext<MyConditionContext, typeof myConditions>({
 *     conditions: myConditions,
 * });
 */
export function createConditionContext<
    TContext,
    TConditions extends ConditionMap<TContext>,
>(config: ConditionConfig<TContext, TConditions>) {
    const ConditionContextReact = createContext<ConditionContextValue<TContext, TConditions> | null>(null);

    type ConditionProviderProps = {
        value: TContext;
        children: ReactNode;
    };

    /**
     * Provider component that makes condition context available to children.
     * Wrap your app or route tree with this provider.
     */
    function ConditionProvider({ value, children }: ConditionProviderProps) {
        const contextValue = useMemo(
            () => ({ context: value, conditions: config.conditions }),
            [value],
        );

        return (
            <ConditionContextReact value={contextValue}>
                {children}
            </ConditionContextReact>
        );
    }

    /**
     * Hook to access the full condition context.
     * Use this when you need access to multiple context fields.
     *
     * @throws Error if used outside of ConditionProvider
     */
    function useConditionContext(): TContext {
        const value = use(ConditionContextReact);
        if (!value) {
            throw new Error('useConditionContext must be used within a ConditionProvider');
        }
        return value.context;
    }

    /**
     * Hook to evaluate a named condition.
     * Returns true if the condition passes, false otherwise.
     *
     * @param name - The name of the condition to evaluate
     * @returns boolean indicating if the condition is met
     *
     * @example
     * const canAccess = useCondition('canAccessQuotation');
     * if (canAccess) {
     *     // render quotation UI
     * }
     */
    function useCondition(name: keyof TConditions): boolean {
        const value = use(ConditionContextReact);
        if (!value) {
            throw new Error('useCondition must be used within a ConditionProvider');
        }
        return evaluateCondition(name as string, value.context, value.conditions);
    }

    /**
     * Hook to get a function that evaluates any condition (named or inline).
     * Useful when working with routes or configs that use the Condition type.
     *
     * @example
     * const evaluate = useConditionEvaluator();
     * const isVisible = route.condition ? evaluate(route.condition) : true;
     */
    function useConditionEvaluator() {
        const value = use(ConditionContextReact);
        if (!value) {
            throw new Error('useConditionEvaluator must be used within a ConditionProvider');
        }

        return useMemo(() => {
            return (condition: string | ((context: TContext) => boolean)): boolean => {
                return evaluateCondition(condition, value.context, value.conditions);
            };
        }, [value]);
    }

    return {
        /**
         * Provider component for condition context.
         */
        ConditionProvider,
        /**
         * Hook to access the full condition context.
         */
        useConditionContext,
        /**
         * Hook to evaluate a named condition.
         */
        useCondition,
        /**
         * Hook to get a condition evaluator function.
         */
        useConditionEvaluator,
        /**
         * The raw React context, for advanced use cases.
         */
        ConditionContext: ConditionContextReact,
        /**
         * The conditions map, for use outside of React (e.g., route guards).
         */
        conditions: config.conditions,
    };
}
